import { useQuery } from "@tanstack/react-query";
import { Download, FileText, Paperclip, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import { isViewable } from "../../shared/files";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  InlineAlert,
} from "../../shared/ui";
import type { AttachmentView } from "../../shared/types/api";

/**
 * Files attached to an entity — a work item or a meeting (§88).
 *
 * <p>The bytes live outside the database, so everything here is metadata. Size and upload time
 * are shown because they are what someone deciding whether to open a file wants to know; the
 * checksum is not, because it answers a question nobody asks on a screen — it exists so a
 * download can be verified and a re-upload of the same file recognised.
 *
 * <p>Downloads go through the API client rather than a link. The access token lives in memory and
 * travels in a header, and an anchor would send neither.
 */
export function AttachmentsCard({
  entityType,
  entityId,
  emptyMessage,
  canRemove = true,
}: {
  entityType: "WORK_ITEM" | "MEETING";
  entityId: string;
  emptyMessage: string;
  canRemove?: boolean;
}) {
  const { can } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const manage = can("ATTACHMENT_MANAGE");

  const attachments = useQuery({
    queryKey: ["attachments", entityType, entityId],
    queryFn: () =>
      api.get<AttachmentView[]>(
        `/api/v1/attachments?entityType=${entityType}&entityId=${entityId}`,
      ),
  });

  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.upload(
        `/api/v1/attachments?entityType=${entityType}&entityId=${entityId}`,
        form,
      );
      void attachments.refetch();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not upload that file.");
    } finally {
      setBusy(false);
      if (inputRef.current) {
        // Cleared so that picking the same file again still fires a change event.
        inputRef.current.value = "";
      }
    }
  };

  const remove = async (attachment: AttachmentView) => {
    setError(null);
    try {
      await api.del(`/api/v1/attachments/${attachment.id}`);
      void attachments.refetch();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not remove that file.");
    }
  };

  /* Called straight from the click, with nothing awaited first: api.view has to open its tab
     while the browser still counts this as the click that asked for it. */
  const open = async (attachment: AttachmentView) => {
    setError(null);
    try {
      await api.view(`/api/v1/attachments/${attachment.id}/content`, attachment.fileName);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not open that file.");
    }
  };

  const save = async (attachment: AttachmentView) => {
    setError(null);
    try {
      await api.download(
        `/api/v1/attachments/${attachment.id}/content`,
        attachment.fileName,
      );
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Could not download that file.",
      );
    }
  };

  if (attachments.isError) {
    return <ErrorState error={attachments.error} onRetry={() => void attachments.refetch()} />;
  }

  const files = attachments.data ?? [];

  if (files.length === 0 && !manage) {
    return null;
  }

  return (
    <Card>
      <CardHeader
        title="Attachments"
        subtitle={files.length > 0 ? `${files.length} file${files.length === 1 ? "" : "s"}` : undefined}
        action={
          manage && (
            <>
              <input
                ref={inputRef}
                type="file"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    void upload(file);
                  }
                }}
              />
              <Button
                variant="ghost"
                size="sm"
                icon={<Upload className="h-4 w-4" />}
                loading={busy}
                onClick={() => inputRef.current?.click()}
              >
                Attach
              </Button>
            </>
          )
        }
      />
      <CardBody className={files.length === 0 ? undefined : "p-0"}>
        {error && (
          <div className={files.length === 0 ? "mb-3" : "px-5 pt-4"}>
            <InlineAlert tone="error">{error}</InlineAlert>
          </div>
        )}

        {files.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-ink-500">
            <Paperclip className="h-4 w-4 shrink-0 text-ink-400" />
            {emptyMessage}
          </p>
        ) : (
          <ul>
            {files.map((file) => (
              <li
                key={file.id}
                className="flex items-center gap-3 border-b border-ink-100 px-5 py-2.5 last:border-0"
              >
                <FileText className="h-4 w-4 shrink-0 text-ink-400" />
                {isViewable(file.fileName, file.contentType) ? (
                  <button
                    type="button"
                    title="Open in a new tab"
                    className="min-w-0 flex-1 truncate text-left text-sm text-brand-700 hover:underline"
                    onClick={() => void open(file)}
                  >
                    {file.fileName}
                  </button>
                ) : (
                  <span className="min-w-0 flex-1 truncate text-sm text-ink-900">
                    {file.fileName}
                  </span>
                )}
                <span className="tnum shrink-0 text-xs text-ink-500">
                  {formatSize(file.sizeBytes)}
                </span>
                <span className="shrink-0 text-xs text-ink-400">
                  {file.uploadedByName ?? "Unknown"}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  title="Download"
                  onClick={() => void save(file)}
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
                {manage && canRemove && (
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Remove"
                    className="text-ink-400 hover:text-red-600"
                    onClick={() => void remove(file)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

/** Bytes at the precision a person reading a file list actually wants. */
function formatSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
